import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Modal de Versiones del Guion.
 *
 * Lista los snapshots con metadatos (escenas, palabras, fecha, notas), y
 * permite:
 *   - Crear nuevo snapshot del guion actual (con nombre + notas opcionales).
 *   - Renombrar snapshot.
 *   - Restaurar snapshot al guion activo (con auto-backup del actual).
 *   - Borrar snapshot (con confirmación).
 *
 * VIVE FUERA del editor protegido. Se invoca desde el botón externo
 * `ScreenplayVersionsButton`.
 */
export default function ScreenplayVersionsModal({
  projectId, currentScreenplay, stats, onClose, onRestored,
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [editing, setEditing] = useState(null); // {id, name, notes}
  const [confirmRestore, setConfirmRestore] = useState(null); // version obj
  const [confirmDelete, setConfirmDelete] = useState(null);   // version id

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get(`/icaa/projects/${projectId}/screenplay-versions`);
      setVersions(r.data.versions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error cargando versiones");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) { setError("Pon un nombre a la versión."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/icaa/projects/${projectId}/screenplay-versions`,
                     { name, notes: newNotes.trim() });
      setCreating(false); setNewName(""); setNewNotes("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error al crear");
    } finally { setBusy(false); }
  };

  const submitRename = async () => {
    if (!editing) return;
    const name = (editing.name || "").trim();
    if (!name) { setError("El nombre no puede estar vacío."); return; }
    setBusy(true); setError("");
    try {
      await api.patch(`/icaa/projects/${projectId}/screenplay-versions/${editing.id}`,
                      { name, notes: editing.notes || "" });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error al renombrar");
    } finally { setBusy(false); }
  };

  const doRestore = async (v, autoBackup = true) => {
    setBusy(true); setError("");
    try {
      const r = await api.post(
        `/icaa/projects/${projectId}/screenplay-versions/${v.id}/restore`,
        { auto_backup: autoBackup },
      );
      const newSp = r.data?.content;
      if (newSp && typeof newSp === "object") {
        onRestored?.(newSp);
      } else {
        setError("La versión no tiene contenido válido.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error al restaurar");
    } finally { setBusy(false); setConfirmRestore(null); }
  };

  const doDelete = async (vid) => {
    setBusy(true); setError("");
    try {
      await api.delete(`/icaa/projects/${projectId}/screenplay-versions/${vid}`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error al borrar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/55 px-4 py-8"
         data-testid="screenplay-versions-modal"
         onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between border-b border-stone-200 px-6 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              ⌖ Versiones del guion
            </p>
            <h2 className="mt-0.5 font-serif text-xl text-[#2C2B29]">Historial independiente</h2>
            <p className="mt-1 text-[11px] text-stone-500">
              Snapshots SÓLO del texto del guion. No alteran casting, dossier ni presupuesto.
              Si restauras una versión vieja, antes se guarda automáticamente el guion actual como backup.
            </p>
          </div>
          <button onClick={onClose} data-testid="screenplay-versions-close"
                  className="rounded-full border border-stone-300 px-2 py-0.5 text-stone-500 hover:border-[#941620] hover:text-[#941620]">
            ✕
          </button>
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-3 border-b border-stone-200 bg-[#FBFAF4] px-6 py-3">
          {!creating ? (
            <button onClick={() => { setNewName(""); setNewNotes(""); setError(""); setCreating(true); }}
                    data-testid="screenplay-versions-new-btn"
                    className="rounded-full bg-[#941620] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#2C2B29]">
              + Guardar guion actual como versión
            </button>
          ) : (
            <div className="flex w-full flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                <input
                  value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                  placeholder="Nombre (ej. v2 · post-notas dirección)"
                  data-testid="screenplay-versions-name"
                  className="flex-1 rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#941620]"
                />
                <button onClick={submitCreate} disabled={busy}
                        data-testid="screenplay-versions-save"
                        className="rounded-full bg-[#26775E] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#2C2B29] disabled:opacity-50">
                  {busy ? "Guardando…" : "Guardar"}
                </button>
                <button onClick={() => setCreating(false)} disabled={busy}
                        className="rounded-full border border-stone-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-stone-600 hover:border-stone-500">
                  Cancelar
                </button>
              </div>
              <textarea
                value={newNotes} onChange={(e) => setNewNotes(e.target.value)}
                placeholder="Notas opcionales: por qué guardas esta versión, qué cambia respecto a la anterior…"
                data-testid="screenplay-versions-notes"
                rows={2}
                className="w-full rounded border border-stone-300 bg-white px-3 py-1.5 text-[12px] outline-none focus:border-[#941620]"
              />
              {stats && (
                <p className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
                  Snapshot del guion actual: {stats.scenes} escenas · {stats.words} palabras · {stats.characters} personajes
                </p>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="border-b border-rose-200 bg-rose-50 px-6 py-2 text-[12px] text-rose-700">
            {error}
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="screenplay-versions-list">
          {loading ? (
            <p className="py-8 text-center text-[11px] uppercase tracking-[0.18em] text-stone-400">Cargando…</p>
          ) : versions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-[#FBFAF4] px-6 py-10 text-center">
              <p className="font-serif text-base text-[#2C2B29]">Aún no hay versiones del guion</p>
              <p className="mt-1 text-[11px] text-stone-500">
                Guarda el primer snapshot cuando termines un borrador o antes de un cambio importante.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {versions.map((v) => (
                <li key={v.id} data-testid={`screenplay-version-row-${v.id}`}
                    className="rounded-xl border border-stone-200 bg-white p-3 transition hover:border-[#2C2B29]/30">
                  {editing?.id === v.id ? (
                    <div className="flex flex-col gap-2">
                      <input value={editing.name}
                             onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                             className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#941620]" />
                      <textarea value={editing.notes || ""} rows={2}
                                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                placeholder="Notas…"
                                className="rounded border border-stone-300 bg-white px-3 py-1.5 text-[12px] outline-none focus:border-[#941620]" />
                      <div className="flex gap-2">
                        <button onClick={submitRename} disabled={busy}
                                className="rounded-full bg-[#26775E] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#2C2B29]">
                          Guardar cambios
                        </button>
                        <button onClick={() => setEditing(null)}
                                className="rounded-full border border-stone-300 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-stone-600 hover:border-stone-500">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-serif text-base text-[#2C2B29]">{v.name}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-stone-500">
                          {fmtDate(v.created_at)} · {v.scenes_count || 0} escenas · {v.words_count || 0} palabras
                        </p>
                        {v.notes && (
                          <p className="mt-1.5 rounded bg-[#FBFAF4] px-2 py-1 text-[12px] italic text-stone-600">
                            {v.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-1">
                        <button onClick={() => setConfirmRestore(v)} disabled={busy}
                                data-testid={`screenplay-version-restore-${v.id}`}
                                className="rounded-full bg-[#941620] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#2C2B29] disabled:opacity-40">
                          ↺ Restaurar
                        </button>
                        <button onClick={() => { setEditing({ id: v.id, name: v.name, notes: v.notes || "" }); setError(""); }}
                                className="rounded-full border border-stone-300 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-stone-600 hover:border-stone-500">
                          ✎ Editar
                        </button>
                        <button onClick={() => setConfirmDelete(v.id)}
                                data-testid={`screenplay-version-delete-${v.id}`}
                                className="rounded-full border border-stone-300 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-stone-500 hover:border-rose-500 hover:text-rose-600">
                          🗑 Borrar
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Confirm restore */}
        {confirmRestore && (
          <div className="border-t border-amber-300 bg-amber-50 px-6 py-3">
            <p className="text-[12px] text-amber-900">
              <strong>¿Restaurar "{confirmRestore.name}"?</strong> Se va a sustituir el texto del guion actual.
              Antes se guardará automáticamente el guion de ahora como un backup en la lista.
            </p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => doRestore(confirmRestore, true)} disabled={busy}
                      data-testid="screenplay-restore-confirm"
                      className="rounded-full bg-[#941620] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#2C2B29] disabled:opacity-40">
                Sí, restaurar
              </button>
              <button onClick={() => setConfirmRestore(null)} disabled={busy}
                      className="rounded-full border border-amber-400 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-amber-800 hover:bg-amber-100">
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Confirm delete */}
        {confirmDelete && (
          <div className="border-t border-rose-300 bg-rose-50 px-6 py-3">
            <p className="text-[12px] text-rose-900">
              <strong>¿Borrar esta versión?</strong> No se puede deshacer.
            </p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => doDelete(confirmDelete)} disabled={busy}
                      className="rounded-full bg-rose-600 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-rose-800 disabled:opacity-40">
                Sí, borrar
              </button>
              <button onClick={() => setConfirmDelete(null)} disabled={busy}
                      className="rounded-full border border-rose-300 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-rose-700 hover:bg-rose-100">
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}
